import React, { FC, useState, useMemo } from "react"; // ✅ useMemo சேர்க்கவும்
import * as Feather from "react-feather";
import { Link } from "react-router-dom";
import "../../styles/SideNavBar.css";
import { useSelector } from "react-redux";
import { StoreState } from "../../redux/reducers";
import { RolePermissionType } from "../../redux/actions/authUserActions";

export interface MenuItemTypes {
  key: string;
  label: string;
  isTitle?: boolean;
  Icon?: Feather.Icon;
  url?: string;
  badge?: {
    variant: string;
    text: string;
  };
  parentKey?: string;
  target?: string;
  children?: MenuItemTypes[];
}

export const MENU_ITEMS: MenuItemTypes[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    isTitle: false,
    Icon: Feather.Airplay,
    url: "/",
  },
  {
    key: "employee",
    label: "Employee",
    url: "/employee",
    Icon: Feather.Users,
  },
  {
    key: "salary",
    label: "Salary",
    isTitle: false,
    Icon: Feather.Repeat,
    children: [
      {
        key: "Salary Master",
        label: "Salary Master",
        url: "/salary_master",
        isTitle: false,
        Icon: Feather.Repeat,
      },
      {
        key: "Employee Salary",
        label: "Employee Salary",
        url: "/employee_salary",
        isTitle: false,
        Icon: Feather.Repeat,
      },
    ],
  },
  {
    key: "leaverequest",
    label: "Leave Request",
    url: "/leave",
    Icon: Feather.Layers,
  },
  {
    key: "leavetransaction",
    label: "Leave Transaction",
    url: "/leave_transaction",
    Icon: Feather.Cast,
  },
  {
    key: "attendence",
    label: "Attendence",
    url: "/attendance_admin",
    Icon: Feather.Repeat,
  },
  {
    key: "dailyreport",
    label: "Daily Report",
    url: "/employee_report",
    Icon: Feather.Anchor,
  },
  {
    key: "task",
    label: "Task",
    url: "/task",
    Icon: Feather.List,
  },
  {
    key: "holiday",
    label: "Holiday",
    isTitle: false,
    Icon: Feather.BarChart,
    url: "/holiday",
  },
  {
    key: "project",
    label: "Project",
    isTitle: false,
    Icon: Feather.Book,
    url: "/project",
  },
  {
    key: "role",
    label: "role",
    isTitle: false,
    Icon: Feather.Book,
    url: "/roles",
  },
  {
    key: "module",
    label: "Module",
    isTitle: false,
    Icon: Feather.MessageSquare,
    url: "/module",
  },
  {
    key: "sub-module",
    label: "Sub-Module",
    isTitle: false,
    Icon: Feather.Server,
    url: "/sub-module",
  },
  {
    key: "page",
    label: "Page",
    isTitle: false,
    Icon: Feather.Paperclip,
    url: "/page",
  },
  {
    key: "sprint",
    label: "Sprint",
    isTitle: false,
    Icon: Feather.Database,
    url: "/sprint",
  },
];

const SideNavBarAdmin: FC = () => {
  const authUser = useSelector((state: StoreState) => state.authUser);
  const userPermission: RolePermissionType[] = authUser?.rolePermissions || [];

  const hasAccess = (key: string): boolean => {
    if (String(authUser?.role).toLowerCase() === "admin") {
      return true;
    }
    return userPermission.some(
      (per) => per.pageKey === key && per.view === true,
    );
  };

  const filterMenuItems = (items: MenuItemTypes[]): MenuItemTypes[] => {
    return items
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);

          if (filteredChildren.length > 0) {
            return { ...item, children: filteredChildren };
          }
          return null;
        }

        return hasAccess(item.key) ? item : null;
      })
      .filter((item): item is MenuItemTypes => item !== null);
  };

  const filteredMenu = useMemo(
    () => filterMenuItems(MENU_ITEMS),
    [userPermission],
  );

  const renderMenuItem = (menuItem: MenuItemTypes) => {
    const { Icon } = menuItem;
    return (
      <li className="nav-item" key={menuItem.key}>
        <Link to={menuItem.url || ""} className="nav-link">
          {Icon && <Icon style={{ marginRight: "5px" }} />}
          <p>{menuItem.label}</p>
        </Link>
      </li>
    );
  };

  return (
    <aside
      className="main-sidebar elevation-4"
      style={{ backgroundColor: "#10296C", position: "fixed" }}
    >
      <div className="sidebar">
        <div className="user-panel d-flex">
          <div className="info">
            <img
              src={
                process.env.PUBLIC_URL
                  ? process.env.PUBLIC_URL + "img/taskTracker.png"
                  : "../img/taskTracker.png"
              }
              style={{ width: "100%" }}
              alt="College Logo"
            />
          </div>
        </div>
        <nav className="mt-2">
          <ul
            className="nav nav-pills nav-sidebar flex-column"
            data-widget="treeview"
            data-accordion="false"
          >
            {filteredMenu?.map((menuItem) =>
              menuItem.children ? (
                <MenuItem key={menuItem.key} menuItem={menuItem} />
              ) : (
                renderMenuItem(menuItem)
              ),
            )}
            <li className="nav-item">
              <Link to="/logout" className="nav-link">
                <Feather.LogOut style={{ marginRight: "5px" }} />
                <p>Logout</p>
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  );
};

const MenuItem: FC<{ menuItem: MenuItemTypes }> = ({ menuItem }) => {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const { Icon } = menuItem;

  const handleToggleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowSubMenu(!showSubMenu);
  };

  return (
    <li className="nav-item" key={menuItem.key}>
      <Link
        to="#"
        className="nav-link"
        onClick={handleToggleMenuClick}
        aria-expanded={showSubMenu}
      >
        {Icon && <Icon style={{ marginRight: "5px" }} />}
        <p>
          {menuItem.label}
          <i
            className={`right fas ${
              !showSubMenu ? "fa-angle-left" : "fa-angle-down"
            }`}
          ></i>
        </p>
      </Link>
      <ul
        className="nav nav-treeview"
        style={{ display: showSubMenu ? "block" : "none" }}
      >
        {menuItem.children?.map((childItem) => (
          <li className="nav-item" key={childItem.key}>
            <Link to={childItem.url || "#"} className="nav-link">
              <i className="fas fa-angle-right nav-icon"></i>
              <p>{childItem.label}</p>
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
};

export default SideNavBarAdmin;
